import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import './PrintReport.css'

/* ── Persian formatting helpers ──────────────────────────────────────────── */
const pNum = (v, digits = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("fa-IR", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });

const pInt = (v) => (v == null ? "—" : Number(v).toLocaleString("fa-IR"));

const todayFa = () =>
  new Date().toLocaleDateString("fa-IR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const signalQuality = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const dbm = Number(value);
  if (dbm >= -60) return "عالی";
  if (dbm >= -70) return "خوب";
  if (dbm >= -80) return "متوسط رو به خوب";
  if (dbm >= -90) return "متوسط";
  if (dbm >= -100) return "ضعیف";
  if (dbm >= -110) return "خیلی ضعیف";
  return "بسیار ضعیف";
};

/* ── Aggregation helpers ─────────────────────────────────────────────────── */
const sumOf = (buckets, key) => {
  const vals = buckets.map((b) => b[key]).filter((v) => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
};

const avgOf = (buckets, key) => {
  const vals = buckets.map((b) => b[key]).filter((v) => v != null);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
};

// Last bucket that actually has a value for `key`.
const latestOf = (buckets, key) => {
  for (let i = buckets.length - 1; i >= 0; i -= 1) {
    if (buckets[i][key] != null) {
      return { value: buckets[i][key], title: buckets[i].title };
    }
  }
  return null;
};

/* ── Print report ────────────────────────────────────────────────────────── */
/**
 * Rendered into document.body through a portal, so that the print CSS can
 * hide every other element of the app (sidebar, navbar, layout wrappers…)
 * with a single `body > *:not(.dap-print-portal)` rule.
 */
export default function PrintReport({
  deviceSerial,
  machineName,
  userName,
  data,
  buckets,
  mapPoints,
  duration,
}) {
  const rows = useMemo(
    () =>
      buckets.map((b) => ({
        ...b,
        perform_time_min: b.perform_time != null ? b.perform_time / 60 : null,
      })),
    [buckets]
  );

  const hasFuel = buckets.some((b) => b.fuel_usage != null);
  const hasDist = buckets.some((b) => b.distance != null);
  const hasPerform = buckets.some((b) => b.perform_time != null);
  const hasSpeed = buckets.some((b) => b.speed != null);
  const hasTemp = buckets.some((b) => b.temp != null);
  const hasSignal = buckets.some((b) => b.cell_signal != null);

  const totalFuel = hasFuel ? sumOf(buckets, "fuel_usage") : null;
  const totalDist = hasDist ? sumOf(buckets, "distance") : null;
  const totalPerformSec = hasPerform ? sumOf(buckets, "perform_time") : null;
  const totalPerformMin = totalPerformSec != null ? totalPerformSec / 60 : null;
  const totalLogs = buckets.reduce((s, b) => s + (b.log_count ?? 0), 0);

  // Averages (no single timestamp belongs to them).
  const avgTemp = hasTemp ? avgOf(buckets, "temp") : null;
  const avgSpeed = hasSpeed ? avgOf(buckets, "speed") : null;
  const avgSignal = hasSignal ? avgOf(buckets, "cell_signal") : null;

  // Latest values (carry the bucket title they were recorded in).
  const latestSignal = latestOf(buckets, "cell_signal");
  const latestTemp = latestOf(buckets, "temp");
  const latestSpeed = latestOf(buckets, "speed");
  const hasLatest = latestSpeed || latestSignal || latestTemp;

  const unitLabel = duration === "day" ? "ساعت" : "بازه";
  // Dash instead of a time for averages (they don't belong to one moment).
  const AVG_TIME = "—";

  return createPortal(
    <div className="dap-print-portal" dir="rtl">
      <div className="dpr-page">
        {/* ── 1. Top: owner + device serial ── */}
        <header className="dpr-header">
          <h1 className="dpr-title">گزارش آنالیز دستگاه</h1>
          <div className="dpr-owner-row">
            <div className="dpr-meta-item">
              <span className="dpr-meta-label">مالک:</span>
              <span className="dpr-meta-value">{userName ?? "—"}</span>
            </div>
            <div className="dpr-meta-item">
              <span className="dpr-meta-label">شماره سریال دستگاه:</span>
              <span className="dpr-meta-value dpr-ltr">
                {deviceSerial ?? "—"}
              </span>
            </div>
            {machineName && (
              <div className="dpr-meta-item">
                <span className="dpr-meta-label">ماشین:</span>
                <span className="dpr-meta-value">{machineName}</span>
              </div>
            )}
          </div>

          {/* ── 2. Under it: the period of the data ── */}
          <div className="dpr-period-row">
            <span className="dpr-meta-label">بازه داده‌ها:</span>
            <span className="dpr-meta-value">{data?.period_label ?? "—"}</span>
          </div>
        </header>

        {/* ── 3. Aggregated values ── */}
        <section className="dpr-section dpr-keep">
          <h2 className="dpr-section-title">مقادیر تجمیعی</h2>
          <table className="dpr-table">
            <thead>
              <tr>
                <th>مصرف سوخت (لیتر)</th>
                <th>مسافت پیموده‌شده (کیلومتر)</th>
                <th>مجموع زمان کارکرد (دقیقه)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="num">{pNum(totalFuel)}</td>
                <td className="num">{pNum(totalDist)}</td>
                <td className="num">{pNum(totalPerformMin, 1)}</td>
              </tr>
            </tbody>
          </table>
          <p className="dpr-note">
            تعداد کل لاگ‌های ثبت‌شده در این بازه: {pInt(totalLogs)}
          </p>
        </section>

        {/* ── 4. Fuel chart ── */}
        {hasFuel && (
          <section className="dpr-section dpr-keep">
            <h2 className="dpr-section-title">
              نمودار مصرف سوخت به تفکیک {unitLabel}
            </h2>
            {/* Fixed size on purpose: ResponsiveContainer measures 0 while the
                report is hidden on screen. ~680px fits an A4 page. */}
            <div className="dpr-chart" dir="ltr">
              <BarChart
                width={680}
                height={240}
                data={rows}
                margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#cfcfcf" />
                <XAxis
                  dataKey="title"
                  tick={{ fill: "#222", fontSize: 10 }}
                  interval={0}
                  angle={rows.length > 12 ? -45 : 0}
                  textAnchor={rows.length > 12 ? "end" : "middle"}
                  height={rows.length > 12 ? 50 : 30}
                />
                <YAxis
                  tick={{ fill: "#222", fontSize: 10 }}
                  tickFormatter={(v) => pNum(v, 0)}
                  width={45}
                />
                <Bar
                  dataKey="fuel_usage"
                  fill="#2f7d3b"
                  isAnimationActive={false}
                />
              </BarChart>
            </div>
            <p className="dpr-note">محور عمودی: مصرف سوخت (لیتر)</p>
          </section>
        )}

        {/* ── 5. Recorded values: latest + averages ── */}
        {hasLatest && (
          <section className="dpr-section dpr-keep">
            <h2 className="dpr-section-title">مقادیر ثبت‌شده</h2>
            <table className="dpr-table">
              <thead>
                <tr>
                  <th>پارامتر</th>
                  <th>مقدار</th>
                  <th>زمان ثبت</th>
                </tr>
              </thead>
              <tbody>
                {/* Latest speed */}
                {latestSpeed && (
                  <tr>
                    <td>آخرین سرعت</td>
                    <td className="num">{pNum(latestSpeed.value)} km/h</td>
                    <td>{latestSpeed.title}</td>
                  </tr>
                )}

                {/* Latest cell signal — time it belongs to */}
                {latestSignal && (
                  <tr>
                    <td>آخرین سیگنال سلولی</td>
                    <td className="num">
                      {pNum(latestSignal.value, 1)} dBm (
                      {signalQuality(latestSignal.value)})
                    </td>
                    <td>{latestSignal.title}</td>
                  </tr>
                )}

                {/* Latest temperature — time it belongs to */}
                {latestTemp && (
                  <tr>
                    <td>آخرین دما</td>
                    <td className="num">{pNum(latestTemp.value, 1)} °C</td>
                    <td>{latestTemp.title}</td>
                  </tr>
                )}

                {/* Averages — no single time, so show a dash */}
                {hasSpeed && avgSpeed != null && (
                  <tr>
                    <td>میانگین سرعت</td>
                    <td className="num">{pNum(avgSpeed)} km/h</td>
                    <td>{AVG_TIME}</td>
                  </tr>
                )}
                {hasSignal && avgSignal != null && (
                  <tr>
                    <td>میانگین سیگنال سلولی</td>
                    <td className="num">
                      {pNum(avgSignal, 1)} dBm (
                      {signalQuality(avgSignal)})
                    </td>
                    <td>{AVG_TIME}</td>
                  </tr>
                )}
                {hasTemp && avgTemp != null && (
                  <tr>
                    <td>میانگین دما</td>
                    <td className="num">{pNum(avgTemp, 1)} °C</td>
                    <td>{AVG_TIME}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="dpr-note">
              ردیف‌های «آخرین» به زمان ثبت همان مقدار اشاره دارند؛
              ردیف‌های «میانگین» روی کل بازه محاسبه شده‌اند و زمان مشخصی
              ندارند.
            </p>
          </section>
        )}

        {/* ── 6. Combined work-time + temperature process ── */}
        {(hasPerform || hasTemp) && (
          <section className="dpr-section">
            <h2 className="dpr-section-title">
              روند زمان کارکرد و دما به تفکیک {unitLabel}
            </h2>
            <table className="dpr-table">
              <thead>
                <tr>
                  <th>{unitLabel}</th>
                  {hasPerform && <th>زمان کارکرد (دقیقه)</th>}
                  {hasTemp && <th>دما (°C)</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((b, i) => (
                  <tr key={`proc-${i}`}>
                    <td>{b.title}</td>
                    {hasPerform && (
                      <td className="num">{pNum(b.perform_time_min, 1)}</td>
                    )}
                    {hasTemp && (
                      <td className="num">{pNum(b.temp, 1)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="dpr-total-label">مجموع / میانگین</td>
                  {hasPerform && (
                    <td className="num">{pNum(totalPerformMin, 1)}</td>
                  )}
                  {hasTemp && <td className="num">{pNum(avgTemp, 1)}</td>}
                </tr>
              </tfoot>
            </table>
            <p className="dpr-note">
              {hasPerform && hasTemp
                ? "ستون زمان کارکرد: مجموع؛ ستون دما: میانگین."
                : hasPerform
                ? "مقدار ردیف پایانی: مجموع زمان کارکرد."
                : "مقدار ردیف پایانی: میانگین دما."}
            </p>
          </section>
        )}

        {/* ── 7. Map points ── */}
        {mapPoints.length > 0 && (
          <section className="dpr-section">
            <h2 className="dpr-section-title">
              نقاط مسیر دستگاه به ترتیب ({pInt(mapPoints.length)} نقطه)
            </h2>
            <table className="dpr-table">
              <thead>
                <tr>
                  <th>ردیف</th>
                  <th>{unitLabel}</th>
                  <th>عرض جغرافیایی (Lat)</th>
                  <th>طول جغرافیایی (Lon)</th>
                </tr>
              </thead>
              <tbody>
                {mapPoints.map((p) => (
                  <tr key={`loc-${p.order}`}>
                    <td className="num">{pInt(p.order)}</td>
                    <td>{p.title ?? "—"}</td>
                    <td className="num ltr">{p.lat.toFixed(6)}</td>
                    <td className="num ltr">{p.lon.toFixed(6)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <footer className="dpr-footer">تاریخ چاپ: {todayFa()}</footer>
      </div>
    </div>,
    document.body
  );
}